/**
 * ContactsLauncher — Contacts directory popup (Round 8 clean rewrite).
 *
 * Opens via CustomEvent('open-contacts-panel') from SiteNav Utilities.
 *
 * People tab:
 *   - Loads first 100 contacts immediately via dedicated /api/contacts endpoint.
 *   - Server-side search (full dataset) with 350ms debounce.
 *   - "Load more" in browse mode when more pages exist.
 *
 * Organizations tab: loaded in full (small table, fast).
 *
 * Click any row → ContactDetailModal (view / edit / create / delete).
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import {
  browseContacts, searchContacts,
  browseOrganizations, searchOrganizations,
  createContact, updateContact, deleteContact,
  createOrganization, updateOrganization, deleteOrganization,
  getContactNamesByIds,
} from '../services/contacts.js'
import ContactDetailModal from './ContactDetailModal.jsx'

/* ── Type colors ────────────────────────────────────────────────────────── */

const TYPE_COLORS = {
  'Active Client': '#1D9E75', 'Active Prospect': '#0d9488', 'Active Lead': '#2563eb',
  'Inactive Lead': '#7a9aaa', 'Team': '#7c3aed', 'Internal Consultant': '#059669',
  'External Consultant': '#6b7280', 'General Contractor': '#d97706',
  'Construction Manager': '#b8540a', 'Subcontractor': '#92400e',
  'Owner Representative': '#ca8a04', 'Vendor': '#0e7490', 'Manufacturer': '#0e7490', 'Reprographics': '#0e7490',
  'Agency': '#c0392b', 'HOA': '#be185d', 'Past Client': '#7a9aaa',
  'Former Team': '#7a9aaa', 'Personal': '#7a9aaa', 'Official': '#7c3aed',
  'Real Estate Agent': '#0284c7', 'Project Stakeholder': '#4338ca',
  'Client': '#1D9E75', 'Prospect': '#0d9488', 'Owner': '#ca8a04',
}

function typeColor(t) { return TYPE_COLORS[t] || '#7a9aaa' }

function typePill(type) {
  return {
    display: 'inline-block', padding: '1px 7px', borderRadius: 20,
    fontFamily: '"DM Mono",monospace', fontSize: 9, fontWeight: 600, letterSpacing: '0.05em',
    background: typeColor(type) + '1a', color: typeColor(type), flexShrink: 0, whiteSpace: 'nowrap',
  }
}

function personName(c) {
  const f = c.fields || {}
  return [f['First Name'], f['Last Name']].filter(Boolean).join(' ') || f['Name'] || '—'
}

function buildOrgMap(orgs) {
  const m = new Map()
  ;(orgs || []).forEach(o => {
    const name = o.fields?.['Organization Name'] || ''
    ;(o.fields?.['Contacts'] || []).forEach(cid => { if (!m.has(cid)) m.set(cid, name) })
  })
  return m
}

/* ── Shared inline styles ───────────────────────────────────────────────── */
/* Themed via the app's existing CSS custom properties (outline.css :root / body.dark) — these
   already flip automatically with the theme, so no useDarkMode() wiring is needed in this file. */

const S = {
  row: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '9px 20px',
    width: '100%', textAlign: 'left', background: 'none', border: 'none',
    borderBottom: '0.5px solid var(--aj-border)', cursor: 'pointer',
    transition: 'background 0.08s',
  },
  name: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sub:  { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  mono: { fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)' },
  pill: (active) => ({
    padding: '3px 10px', borderRadius: 20, fontFamily: '"DM Mono",monospace', fontSize: 10,
    fontWeight: 600, letterSpacing: '0.05em', cursor: 'pointer', border: 'none', flexShrink: 0,
    background: active ? '#07272D' : 'var(--aj-bg-muted)',
    color: active ? '#fff' : 'var(--aj-text-mid)',
  }),
  tab: (active) => ({
    padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 13,
    fontWeight: active ? 600 : 400, color: active ? 'var(--aj-text)' : 'var(--aj-text-muted)',
    background: 'none', border: 'none', cursor: 'pointer', marginBottom: -1,
    borderBottom: active ? '2px solid #07272D' : '2px solid transparent',
  }),
}

/* ── Row components ─────────────────────────────────────────────────────── */

function PersonRow({ contact, orgName, onClick }) {
  const f = contact.fields || {}
  const type = f['Contact Type'] || ''
  return (
    <button style={S.row} onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--aj-bg-subtle)' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'none' }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"DM Mono",monospace', fontSize: 11, fontWeight: 700, background: typeColor(type) + '22', color: typeColor(type) }}>
        {personName(contact).split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={S.name}>{personName(contact)}</span>
          {type && <span style={typePill(type)}>{type}</span>}
        </div>
        <div style={S.sub}>{[f['Job Title'], orgName].filter(Boolean).join(' · ')}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
        {f['Email'] && <span style={S.mono}>{f['Email']}</span>}
        {f['Phone'] && <span style={S.mono}>{f['Phone']}</span>}
      </div>
    </button>
  )
}

function OrgRow({ org, contacts, primaryNames, onClick }) {
  const f = org.fields || {}
  const type = Array.isArray(f['Type']) ? (f['Type'][0] || '') : (f['Type'] || '')
  const primaryId = (f['Primary Contact'] || [])[0]
  const primary = (contacts || []).find(c => c.id === primaryId)
  // Prefer the loaded record's name; fall back to the by-id resolved map (covers primaries beyond
  // the first browse page, which otherwise showed blank even though the link exists).
  const primaryName = primary ? personName(primary) : (primaryNames?.[primaryId] || '')
  return (
    <button style={S.row} onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--aj-bg-subtle)' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'none' }}>
      <div style={{ width: 32, height: 32, borderRadius: 6, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--aj-bg-muted)', fontSize: 14 }}>🏢</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={S.name}>{f['Organization Name'] || '—'}</span>
          {type && <span style={typePill(type)}>{type}</span>}
        </div>
        <div style={S.sub}>{primaryName}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
        {f['General Email'] && <span style={S.mono}>{f['General Email']}</span>}
        {f['Phone'] && <span style={S.mono}>{f['Phone']}</span>}
      </div>
    </button>
  )
}

/* ── Generic paginated list hook — normalized store ─────────────────────────
 * ONE copy of each record (byId); browse and search are just ID LISTS pointing into it, and the
 * arrays the component renders are materialized from those ids. The previous design held full
 * record objects in two places (records + searchResult), so every mutation had to remember to
 * patch both — forgetting one produced the "edit saved but the screen still shows the old value
 * until a hard refresh" bug class (three separate instances found: edit, delete, and create,
 * all while a search was active — which is the common case, since the search box autofocuses).
 * With record content stored exactly once, an edit patches byId in one place and every view
 * reflects it; membership (which ids appear where) is all that's left per-view, and delete
 * removes the id from both lists in one function.
 */
function useContactList(browseFn, searchFn) {
  const [byId, setById]             = useState({})
  const [browseIds, setBrowseIds]   = useState([])
  const [searchIds, setSearchIds]   = useState(null)   // null = not searching
  const [nextOffset, setNextOffset] = useState(null)
  const [loading, setLoading]       = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [searching, setSearching]   = useState(false)
  const debounceRef = useRef(null)
  const lastQueryRef = useRef('')

  const absorb = useCallback((recs) => {
    setById(prev => {
      const next = { ...prev }
      ;(recs || []).forEach(r => { if (r && r.id) next[r.id] = r })
      return next
    })
  }, [])

  // `keepIds` stay pinned at the top after the reload. The browse page is the first 100 records
  // alphabetically, so without this a just-created record whose name sorts later ("Niguel
  // Summit") vanished the moment it was saved and looked like the save had failed (2026-09-30).
  const loadFirst = useCallback(async (keepIds = []) => {
    setLoading(true)
    try {
      const { contacts: recs, nextOffset: off } = await browseFn()
      absorb(recs)
      const page = (recs || []).map(r => r.id)
      setBrowseIds([...keepIds.filter(id => !page.includes(id)), ...page])
      setNextOffset(off || null)
    } catch { setBrowseIds([]) }
    finally { setLoading(false) }
  }, [browseFn, absorb])

  const loadMore = useCallback(async () => {
    if (!nextOffset || loadingMore) return
    setLoadingMore(true)
    try {
      const { contacts: recs, nextOffset: off } = await browseFn(nextOffset)
      absorb(recs)
      setBrowseIds(prev => [...prev, ...(recs || []).map(r => r.id).filter(id => !prev.includes(id))])
      setNextOffset(off || null)
    } catch { /* non-fatal */ }
    finally { setLoadingMore(false) }
  }, [browseFn, nextOffset, loadingMore, absorb])

  const runSearch = useCallback(async (q) => {
    try {
      const { contacts: recs } = await searchFn(q)
      absorb(recs)
      setSearchIds((recs || []).map(r => r.id))
    } catch { setSearchIds([]) }
    finally { setSearching(false) }
  }, [searchFn, absorb])

  const search = useCallback((q) => {
    clearTimeout(debounceRef.current)
    lastQueryRef.current = q.trim()
    if (!q.trim()) { setSearchIds(null); return }
    setSearching(true)
    debounceRef.current = setTimeout(() => runSearch(q.trim()), 350)
  }, [runSearch])

  /* Re-run the active search immediately (no debounce) — used after a CREATE, where local code
   * can't know whether the new record matches the current query; the server decides. Not used
   * after delete (removeRecord already handles membership locally, and a server re-fetch could
   * theoretically resurrect a just-deleted row if the index lags). */
  const refreshSearch = useCallback(() => {
    if (!lastQueryRef.current) return
    clearTimeout(debounceRef.current)
    setSearching(true)
    runSearch(lastQueryRef.current)
  }, [runSearch])

  const updateRecord = useCallback((updated) => {
    if (!updated || !updated.id) return
    setById(prev => ({ ...prev, [updated.id]: updated }))
  }, [])

  const addRecord = useCallback((record) => {
    if (!record || !record.id) return
    absorb([record])
    setBrowseIds(prev => prev.includes(record.id) ? prev : [record.id, ...prev])
  }, [absorb])

  const removeRecord = useCallback((id) => {
    if (!id) return
    setById(prev => { const next = { ...prev }; delete next[id]; return next })
    setBrowseIds(prev => prev.filter(x => x !== id))
    setSearchIds(prev => prev ? prev.filter(x => x !== id) : prev)
  }, [])

  // Materialized views — same shapes/names the component consumed before the normalization, so
  // the render code below needed no changes. filter(Boolean) drops ids whose record was removed.
  const records      = useMemo(() => browseIds.map(id => byId[id]).filter(Boolean), [browseIds, byId])
  const searchResult = useMemo(() => searchIds ? searchIds.map(id => byId[id]).filter(Boolean) : null, [searchIds, byId])

  return { records, nextOffset, loading, loadingMore, searchResult, searching, loadFirst, loadMore, search, refreshSearch, updateRecord, addRecord, removeRecord }
}

/* ── Main component ─────────────────────────────────────────────────────── */

export default function ContactsLauncher() {
  const [open, setOpen]     = useState(false)
  const [tab, setTab]       = useState('people')
  const [search, setSearch] = useState('')
  const [modal, setModal]   = useState(null)

  const people = useContactList(browseContacts, searchContacts)
  const orgs   = useContactList(browseOrganizations, searchOrganizations)

  // Resolve primary-contact names for orgs whose primary isn't in the loaded People page, so the
  // org list subtitle isn't blank for contacts past the first 100.
  const [primaryNames, setPrimaryNames] = useState({})

  /* Open / close */
  useEffect(() => {
    function onOpen() {
      setOpen(true); setTab('people'); setSearch(''); setModal(null)
    }
    window.addEventListener('open-contacts-panel', onOpen)
    return () => window.removeEventListener('open-contacts-panel', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open && !modal) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, modal])

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  /* Load data when opened */
  useEffect(() => {
    if (!open) return
    people.loadFirst()
    orgs.loadFirst()
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Trigger server-side search as user types (both tabs) */
  useEffect(() => {
    people.search(search)
    orgs.search(search)
  }, [search]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Resolve any org primary-contact names not already in the loaded People page (batched, by id). */
  useEffect(() => {
    if (!open) return
    const have = new Set(people.records.map(c => c.id))
    const need = []
    ;[...orgs.records, ...(orgs.searchResult || [])].forEach(o => {
      const pid = (o.fields?.['Primary Contact'] || [])[0]
      if (pid && !have.has(pid) && !primaryNames[pid]) need.push(pid)
    })
    if (!need.length) return
    getContactNamesByIds(need.slice(0, 100)).then(map => {
      if (map && Object.keys(map).length) setPrimaryNames(prev => ({ ...prev, ...map }))
    }).catch(() => {})
  }, [open, orgs.records, orgs.searchResult, people.records]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null

  const orgMap = buildOrgMap(orgs.records)
  const isSearching = !!search.trim()

  const peopleList = isSearching ? (people.searchResult || []) : people.records
  const orgList    = isSearching ? (orgs.searchResult   || []) : orgs.records

  /* Count label: show loaded count + "+" if more pages exist, no number when searching */
  function countLabel(list) {
    if (!list) return ''
    return isSearching ? ` (${list.length})` : ` (${list.length}${list.nextOffset ? '+' : ''})`
  }

  // ContactDetailModal lets you set ANY contact as an org's Primary Contact, not just an existing
  // member — see that file's 2026-08 note. Whichever direction the save came from (update or
  // create), if the chosen primary isn't already linked into this org's Contacts, link them for
  // real (the same write the "add member" action uses) right after the org itself saves, so the
  // two fields can never actually drift apart because of an in-app edit.
  async function syncPrimaryContactMembership(orgRecord, fields) {
    const primaryId = (fields['Primary Contact'] || [])[0]
    if (!primaryId) return orgRecord
    const existing = (orgRecord.fields && orgRecord.fields['Contacts']) || []
    if (existing.includes(primaryId)) return orgRecord
    await updateContact(primaryId, { 'Organization Contacts': [orgRecord.id] })
    return { ...orgRecord, fields: { ...orgRecord.fields, Contacts: existing.concat(primaryId) } }
  }

  /* Modals — every mutation goes through the normalized store (see useContactList), so the
     visible list is correct immediately whether the user is browsing or mid-search. */
  async function handleSave(fields) {
    const { kind, record } = modal
    const list = kind === 'contact' ? people : orgs
    if (record) {
      // The Airtable PATCH response is the full updated record — one byId patch covers every view.
      let updated = kind === 'contact'
        ? await updateContact(record.id, fields)
        : await updateOrganization(record.id, fields)
      if (kind === 'org') updated = await syncPrimaryContactMembership(updated, fields)
      list.updateRecord(updated)
    } else {
      let created = kind === 'contact' ? await createContact(fields) : await createOrganization(fields)
      if (kind === 'org') created = await syncPrimaryContactMembership(created, fields)
      list.addRecord(created)
      list.refreshSearch()   // if it matches the active search, it shows up without retyping
      setModal(null)
      list.loadFirst([created.id])   // keep the new record visible at the top
      return
    }
    setModal(null)
    list.loadFirst()
  }
  async function handleDelete(id) {
    const list = modal.kind === 'contact' ? people : orgs
    if (modal.kind === 'contact') await deleteContact(id)
    else await deleteOrganization(id)
    list.removeRecord(id)   // gone from browse AND search immediately
    list.loadFirst()
    setModal(null)
  }

  const linkedOrgIds = (modal?.kind === 'contact' && modal.record)
    ? [orgs.records.find(o => (o.fields?.['Contacts'] || []).includes(modal.record.id))?.id].filter(Boolean)
    : []

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 800, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div data-modal-id="ContactsLauncher" style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)', boxShadow: '0 16px 48px rgba(0,0,0,0.35)', width: 720, maxWidth: '94vw', height: 'min(80vh, 740px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* ── Header ── */}
        <div style={{ padding: '16px 20px 0', borderBottom: '0.5px solid var(--aj-border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--aj-text)' }}>Contacts</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Search — live, server-side for People */}
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  autoFocus
                  style={{ padding: '5px 28px 5px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 4, outline: 'none', width: 210, color: 'var(--aj-text)' }}
                />
                {search && (
                  <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                )}
              </div>
              <button
                onClick={() => setModal({ mode: 'create', kind: tab === 'people' ? 'contact' : 'org', record: null })}
                style={{ padding: '5px 12px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: '#07272D', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
              >
                + {tab === 'people' ? 'Add Person' : 'Add Organization'}
              </button>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 18, lineHeight: 1, padding: 2 }}>×</button>
            </div>
          </div>
          <div style={{ display: 'flex' }}>
            <button style={S.tab(tab === 'people')} onClick={() => { setTab('people'); setSearch('') }}>
              People
              {!people.loading && <span style={{ opacity: 0.45, fontWeight: 400 }}>{!isSearching && people.nextOffset ? ` (${people.records.length}+)` : ` (${peopleList.length})`}</span>}
            </button>
            <button style={S.tab(tab === 'orgs')} onClick={() => { setTab('orgs'); setSearch('') }}>
              Organizations
              {!orgs.loading && <span style={{ opacity: 0.45, fontWeight: 400 }}>{!isSearching && orgs.nextOffset ? ` (${orgs.records.length}+)` : ` (${orgList.length})`}</span>}
            </button>
          </div>
        </div>

        {/* ── List ── */}
        <div style={{ flex: 1, overflowY: 'auto' }}>

          {/* People */}
          {tab === 'people' && (
            <>
              {people.loading && !people.records.length && <div style={fillerStyle}>Loading…</div>}
              {people.searching && <div style={{ ...fillerStyle, color: 'var(--aj-text-muted)' }}>Searching…</div>}
              {!people.loading && !people.searching && peopleList.length === 0 && (
                <div style={{ ...fillerStyle, fontStyle: 'italic' }}>{isSearching ? 'No matches.' : 'No contacts loaded.'}</div>
              )}
              {!people.searching && peopleList.map(c => (
                <PersonRow key={c.id} contact={c} orgName={orgMap.get(c.id) || ''}
                  onClick={() => setModal({ mode: 'view', kind: 'contact', record: c })} />
              ))}
              {!isSearching && people.nextOffset && !people.loading && (
                <div style={{ padding: '12px 20px', textAlign: 'center' }}>
                  <button onClick={people.loadMore} disabled={people.loadingMore}
                    style={{ padding: '6px 18px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', border: '0.5px solid var(--aj-border)', borderRadius: 4, cursor: people.loadingMore ? 'default' : 'pointer' }}>
                    {people.loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Organizations — same pagination pattern as People */}
          {tab === 'orgs' && (
            <>
              {orgs.loading && !orgs.records.length && <div style={fillerStyle}>Loading…</div>}
              {orgs.searching && <div style={{ ...fillerStyle, color: 'var(--aj-text-muted)' }}>Searching…</div>}
              {!orgs.loading && !orgs.searching && orgList.length === 0 && (
                <div style={{ ...fillerStyle, fontStyle: 'italic' }}>{isSearching ? 'No matches.' : 'No organizations.'}</div>
              )}
              {!orgs.searching && orgList.map(o => (
                <OrgRow key={o.id} org={o} contacts={people.records} primaryNames={primaryNames}
                  onClick={() => setModal({ mode: 'view', kind: 'org', record: o })} />
              ))}
              {!isSearching && orgs.nextOffset && !orgs.loading && (
                <div style={{ padding: '12px 20px', textAlign: 'center' }}>
                  <button onClick={orgs.loadMore} disabled={orgs.loadingMore}
                    style={{ padding: '6px 18px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', border: '0.5px solid var(--aj-border)', borderRadius: 4, cursor: orgs.loadingMore ? 'default' : 'pointer' }}>
                    {orgs.loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Detail modal */}
      {modal && (
        <ContactDetailModal
          mode={modal.mode}
          kind={modal.kind}
          record={modal.record}
          linkedOrgIds={linkedOrgIds}
          contacts={people.records}
          orgs={orgs.records}
          typePill={typePill}
          onEdit={() => setModal({ ...modal, mode: 'edit' })}
          onClose={() => setModal(null)}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      )}
    </div>,
    document.body
  )
}

const fillerStyle = { padding: 40, textAlign: 'center', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' }
